/**
 * TSL materials of the scaled-space pass (M3 plan R8): the star (limb-darkened, granulated emissive sphere, a corona
 * billboard, a bounded glare sprite and one lens-flare streak), the four planet kinds, the Fresnel atmosphere shell
 * and the banded ring. Placeholder-grade looks, built so they can grow without an API change:
 *
 * - Every per-body value (palette, noise parameters, seed offset) is a uniform, so all bodies of one kind share one
 *   shader and one pipeline; a system switch compiles at most one pipeline per kind.
 * - Noise is the in-house `sky/noise.ts` fbm (emitted once as a function), never MaterialX's inlined loops.
 * - Meshes are unit spheres and rings in planet radii: the body group's scale carries the radius, so the shaders
 *   work in unit space (`positionLocal`) and the sphere LOD can swap geometry without touching a material.
 */
import {
  AdditiveBlending,
  Color,
  DoubleSide,
  MeshBasicNodeMaterial,
  MeshStandardNodeMaterial,
  Vector3,
  type Node,
  type UniformNode,
} from 'three/webgpu'
import {
  abs,
  cameraPosition,
  cameraProjectionMatrix,
  dot,
  exp,
  float,
  length,
  max,
  mix,
  modelViewMatrix,
  normalize,
  normalWorld,
  positionGeometry,
  positionLocal,
  positionWorld,
  pow,
  saturate,
  select,
  sin,
  smoothstep,
  uniform,
  uv,
  varying,
  vec2,
  vec3,
  vec4,
  viewportSize,
} from 'three/tsl'
import type { BodyKind } from '../../galaxy/types'
import { createRng, range } from '../../core/rng'
import { fbm3, fbmVec3, noise3 } from '../environment/sky/noise'
import type { AtmosphereLook, PlanetLook, ScaledConfig } from './config'

type FloatNode = Node<'float'>
type Vec2Node = Node<'vec2'>
type Vec4Node = Node<'vec4'>
type FloatUniform = UniformNode<'float', number>
type ColorUniform = UniformNode<'color', Color>
type Vec3Uniform = UniformNode<'vec3', Vector3>

/** Uniforms every scaled material reads: the star position (scaled space), time and the dev-panel gains. */
export interface SharedUniforms {
  readonly starPos: Vec3Uniform
  /** Seconds, wrapped (float32-safe) by the pass. */
  readonly time: FloatUniform
  readonly atmosphereGain: FloatUniform
  readonly ringGain: FloatUniform
}

export function createSharedUniforms(): SharedUniforms {
  return { starPos: uniform(new Vector3()), time: uniform(0), atmosphereGain: uniform(1), ringGain: uniform(1) }
}

/** A per-body noise offset (seeded), so two bodies of one kind never look alike. */
export function seedOffset(seed: number, out: Vector3): Vector3 {
  const rng = createRng((seed ^ 0x51ca1ed) >>> 0)
  return out.set(range(rng, 0, 100), range(rng, 0, 100), range(rng, 0, 100))
}

// ---------------------------------------------------------------------------------------------------------------
// Planets
// ---------------------------------------------------------------------------------------------------------------

export interface PlanetUniforms {
  readonly palette: readonly [ColorUniform, ColorUniform, ColorUniform, ColorUniform]
  readonly offset: Vec3Uniform
  readonly scale: FloatUniform
  readonly threshold: FloatUniform
  readonly detail: FloatUniform
  readonly emissive: FloatUniform
}

export interface PlanetMaterial {
  readonly material: MeshStandardNodeMaterial
  readonly uniforms: PlanetUniforms
}

/** The surface material of one body: the kind picks the shader, the look and seed fill its uniforms. */
export function createPlanetMaterial(kind: BodyKind, look: PlanetLook, seed: number): PlanetMaterial {
  const u: PlanetUniforms = {
    palette: [uniform(new Color(look.palette[0])), uniform(new Color(look.palette[1])), uniform(new Color(look.palette[2])), uniform(new Color(look.palette[3]))],
    offset: uniform(seedOffset(seed, new Vector3())),
    scale: uniform(look.noise.scale),
    threshold: uniform(look.noise.threshold),
    detail: uniform(look.noise.detail),
    emissive: uniform(look.emissive),
  }
  const m = new MeshStandardNodeMaterial({ roughness: look.roughness, metalness: 0 })
  m.name = `scaled:${kind}`
  const p = normalize(positionLocal)
  const q = p.mul(u.scale).add(u.offset)
  const [c0, c1, c2, c3] = u.palette
  const oct = look.noise.octaves
  const gain = look.noise.gain
  switch (kind) {
    case 'rocky': {
      const n = fbm3(q, oct, gain)
      const land = smoothstep(u.threshold.sub(0.04), u.threshold.add(0.04), n)
      const d = noise3(p.mul(u.detail).add(u.offset.zxy)).mul(0.5).add(0.5)
      const ground = mix(c1, c2, saturate(d.add(n.mul(0.3))))
      const shore = mix(c0, ground, land)
      const ice = smoothstep(0.72, 0.88, abs(p.y).add(n.mul(0.08)))
      m.colorNode = mix(shore, c3, ice)
      break
    }
    case 'icy': {
      const n = saturate(fbm3(q, oct, gain).mul(0.5).add(0.5))
      const base = mix(c0, c1, n)
      const crack = float(1).sub(smoothstep(0, u.threshold, abs(noise3(p.mul(u.detail).add(u.offset.yzx)))))
      const frost = smoothstep(0.6, 0.95, abs(p.y))
      m.colorNode = mix(mix(base, c2, crack.mul(0.8)), c3, frost)
      break
    }
    case 'lava': {
      const n = saturate(fbm3(q, oct, gain).mul(0.5).add(0.5))
      const crust = mix(c0, c1, n)
      const crack = float(1).sub(smoothstep(0, u.threshold, abs(fbm3(p.mul(u.detail).add(u.offset.zxy), 3, 0.5))))
      m.colorNode = mix(crust, c0.mul(0.5), crack)
      m.emissiveNode = mix(c2, c3, crack.mul(crack)).mul(crack).mul(u.emissive)
      break
    }
    case 'gas': {
      const w = fbmVec3(q.mul(vec3(1, 3, 1)), oct, gain)
      const lat = p.y.add(w.x.mul(u.threshold))
      const bands = sin(lat.mul(u.detail).mul(Math.PI)).mul(0.5).add(0.5)
      const broad = sin(lat.mul(u.detail).mul(Math.PI * 0.37).add(1.3)).mul(0.5).add(0.5)
      const belt = mix(c0, c1, broad)
      const zone = smoothstep(0.35, 0.75, bands.add(w.y.mul(0.15)))
      const accent = smoothstep(0.7, 0.95, broad).mul(0.5)
      m.colorNode = mix(mix(belt, c2, zone), c3, accent)
      break
    }
  }
  return { material: m, uniforms: u }
}

// ---------------------------------------------------------------------------------------------------------------
// Atmosphere and rings
// ---------------------------------------------------------------------------------------------------------------

export interface AtmosphereMaterial {
  readonly material: MeshBasicNodeMaterial
  readonly uniforms: { color: ColorUniform; power: FloatUniform; intensity: FloatUniform }
}

/**
 * A Fresnel shell (front faces, additive): bright at the limb, lit on the star's side. Drawn on a unit sphere
 * scaled to `look.scale` planet radii.
 */
export function createAtmosphereMaterial(look: AtmosphereLook, shared: SharedUniforms): AtmosphereMaterial {
  const u = { color: uniform(new Color(look.color)), power: uniform(look.power), intensity: uniform(look.intensity) }
  const m = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending })
  m.name = 'scaled:atmosphere'
  const n = normalize(normalWorld)
  const v = normalize(cameraPosition.sub(positionWorld))
  const l = normalize(shared.starPos.sub(positionWorld))
  const fres = pow(float(1).sub(saturate(dot(n, v))), u.power)
  const lit = smoothstep(-0.25, 0.35, dot(n, l))
  m.colorNode = u.color.mul(u.intensity).mul(shared.atmosphereGain).mul(fres).mul(lit)
  return { material: m, uniforms: u }
}

export interface RingMaterial {
  readonly material: MeshStandardNodeMaterial
  readonly uniforms: { color: ColorUniform; opacity: FloatUniform; bands: FloatUniform; seed: FloatUniform }
}

/** Banded, lit ring on a RingGeometry in planet radii (inner..outer from the config), lying in local XZ. */
export function createRingMaterial(rings: ScaledConfig['rings'], seed: number, shared: SharedUniforms): RingMaterial {
  const rng = createRng((seed ^ 0x4199) >>> 0)
  const u = { color: uniform(new Color(rings.color)), opacity: uniform(rings.opacity), bands: uniform(rings.bands), seed: uniform(range(rng, 0, 100)) }
  const m = new MeshStandardNodeMaterial({ side: DoubleSide, transparent: true, depthWrite: false, roughness: 1, metalness: 0 })
  m.name = 'scaled:ring'
  const r = length(vec2(positionLocal.x, positionLocal.z))
  const t = saturate(r.sub(rings.inner).div(rings.outer - rings.inner))
  const band = noise3(vec3(t.mul(u.bands), u.seed, 0.5)).mul(0.5).add(0.5)
  const gap = float(1).sub(smoothstep(0.03, 0, abs(t.sub(0.64))).mul(0.8))
  const edges = smoothstep(0, 0.06, t).mul(float(1).sub(smoothstep(0.9, 1, t)))
  m.colorNode = u.color.mul(float(0.75).add(band.mul(0.25)))
  m.opacityNode = saturate(band.mul(1.3).sub(0.15)).mul(gap).mul(edges).mul(u.opacity).mul(shared.ringGain)
  return { material: m, uniforms: u }
}

// ---------------------------------------------------------------------------------------------------------------
// Star
// ---------------------------------------------------------------------------------------------------------------

export interface StarUniforms {
  readonly color: ColorUniform
  readonly intensity: FloatUniform
  /** Star radius in scaled units (the corona billboard's size). */
  readonly radius: FloatUniform
  readonly offset: Vec3Uniform
  readonly corona: FloatUniform
  readonly glare: FloatUniform
  readonly streak: FloatUniform
}

export interface StarMaterials {
  readonly surface: MeshBasicNodeMaterial
  readonly corona: MeshBasicNodeMaterial
  readonly glare: MeshBasicNodeMaterial
  readonly streak: MeshBasicNodeMaterial
  readonly uniforms: StarUniforms
  dispose(): void
}

/** Clip position of the object's origin. */
function centreClip(): Vec4Node {
  return cameraProjectionMatrix.mul(modelViewMatrix.mul(vec4(0, 0, 0, 1)))
}

/** A unit quad expanded in NDC around the object's origin by `half`, keeping the origin's depth. */
function screenQuad(half: Vec2Node): Vec4Node {
  const c = centreClip()
  const offset = positionGeometry.xy.mul(2).mul(half).mul(c.w)
  return vec4(c.xy.add(offset), c.z, c.w)
}

/** 1 while the origin is on screen and in front of the camera, fading to 0 just past the edges. */
function onScreen(): FloatNode {
  const c = centreClip()
  const ndc = c.xy.div(max(c.w, 1e-6))
  const edge = max(abs(ndc.x), abs(ndc.y))
  return select(c.w.greaterThan(0), float(1).sub(smoothstep(1.0, 1.2, edge)), float(0))
}

export function createStarMaterials(cfg: ScaledConfig['star'], shared: SharedUniforms): StarMaterials {
  const u: StarUniforms = {
    color: uniform(new Color(1, 1, 1)),
    intensity: uniform(cfg.intensity),
    radius: uniform(1),
    offset: uniform(new Vector3()),
    corona: uniform(cfg.corona.intensity),
    glare: uniform(cfg.glare.intensity),
    streak: uniform(cfg.streak.intensity),
  }

  // Surface: limb darkening I(μ) = 1 − u(1 − μ^0.6) and slowly boiling granulation.
  const surface = new MeshBasicNodeMaterial()
  surface.name = 'scaled:star'
  const p = normalize(positionLocal)
  const n = normalize(normalWorld)
  const v = normalize(cameraPosition.sub(positionWorld))
  const mu = saturate(dot(n, v))
  const limb = float(1).sub(float(cfg.limbDarkening).mul(float(1).sub(pow(mu, 0.6))))
  const g = cfg.granulation
  const gran = fbm3(p.mul(g.scale).add(u.offset).add(vec3(shared.time.mul(g.speed))), 3, 0.5)
  surface.colorNode = u.color.mul(u.intensity).mul(limb).mul(float(1).add(gran.mul(g.amount)))

  // Corona: a view-facing billboard at the star centre, falling off outside the disc (the sphere hides its core).
  const corona = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending })
  corona.name = 'scaled:corona'
  const k = cfg.corona.scale
  const viewCentre = modelViewMatrix.mul(vec4(0, 0, 0, 1))
  corona.vertexNode = cameraProjectionMatrix.mul(vec4(viewCentre.xyz.add(vec3(positionGeometry.xy.mul(u.radius.mul(2 * k)), 0)), 1))
  const d = length(uv().sub(0.5)).mul(2 * k) // in star radii
  const halo = exp(max(d.sub(1), 0).mul(-cfg.corona.falloff)).mul(float(1).sub(smoothstep(k * 0.75, k, d)))
  corona.colorNode = u.color.mul(u.corona).mul(halo)

  // Glare: bounded radius (fraction of the screen height), 1/r² core, fades as the star leaves the view.
  const glare = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending })
  glare.name = 'scaled:glare'
  const aspect = viewportSize.x.div(viewportSize.y)
  const gl = cfg.glare
  glare.vertexNode = screenQuad(vec2(float(2 * gl.maxRadius).div(aspect), 2 * gl.maxRadius))
  const dg = length(uv().sub(0.5)).mul(2)
  const w2 = gl.core * gl.core
  const falloff = float(w2).div(dg.mul(dg).add(w2))
  const win = pow(saturate(float(1).sub(dg)), 2)
  const glareTint = new Color(gl.color)
  glare.colorNode = vec3(glareTint.r, glareTint.g, glareTint.b).mul(u.color).mul(u.glare).mul(falloff).mul(win).mul(varying(onScreen(), 'vScaledGlare'))

  // One anamorphic lens-flare streak.
  const streak = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending })
  streak.name = 'scaled:streak'
  const st = cfg.streak
  streak.vertexNode = screenQuad(vec2(st.length, 2 * st.thickness))
  const sx = abs(uv().x.sub(0.5)).mul(2)
  const sy = abs(uv().y.sub(0.5)).mul(2)
  const along = exp(sx.mul(-2.2)).mul(pow(float(1).sub(sx), 1.5))
  const across = exp(sy.mul(sy).mul(-14)).mul(float(1).sub(sy))
  const streakTint = new Color(st.color)
  streak.colorNode = vec3(streakTint.r, streakTint.g, streakTint.b).mul(u.streak).mul(along).mul(across).mul(varying(onScreen(), 'vScaledStreak'))

  return {
    surface,
    corona,
    glare,
    streak,
    uniforms: u,
    dispose(): void {
      surface.dispose()
      corona.dispose()
      glare.dispose()
      streak.dispose()
    },
  }
}
